const FULLWIDTH_ASCII_START = 0xff01;
const FULLWIDTH_ASCII_END = 0xff5e;
const FULLWIDTH_ASCII_OFFSET = 0xfee0;
const DASH_VARIANTS = new Set([
  "\u2010",
  "\u2011",
  "\u2012",
  "\u2013",
  "\u2014",
  "\u2015",
  "\u2212",
  "\u30fc",
  "\uff0d",
  "\uff70",
]);
const SPACE_VARIANTS = new Set(["\u00a0", "\u2007", "\u202f", "\u3000"]);

export const PROFILE_MEASUREMENT_LIMITS = Object.freeze({
  height: Object.freeze({
    min: 60,
    max: 260,
    messageKey: "form.validation.heightOutOfRange",
  }),
  weight: Object.freeze({
    min: 20,
    max: 400,
    messageKey: "form.validation.weightOutOfRange",
  }),
  chestCircumference: Object.freeze({
    min: 30,
    max: 200,
    messageKey: "form.validation.chestCircumferenceOutOfRange",
  }),
});

const EXPLICIT_CENTIMETRE_PATTERN = /^([+-]?(?:\d+(?:\.\d+)?|\.\d+))\s*cm$/iu;
const EXPLICIT_METRE_PATTERN = /^([+-]?(?:\d+(?:\.\d+)?|\.\d+))\s*m$/iu;
const DECIMAL_NUMBER_PATTERN = /^[+-]?(?:\d+\.\d+|\.\d+)$/u;

function normalizeKeyboardCompatibleCharacters(value) {
  if (value === undefined || value === null) {
    return "";
  }

  let normalized = "";

  for (const char of String(value)) {
    const codePoint = char.codePointAt(0);

    if (SPACE_VARIANTS.has(char)) {
      normalized += " ";
      continue;
    }

    if (DASH_VARIANTS.has(char)) {
      normalized += "-";
      continue;
    }

    if (
      codePoint !== undefined &&
      codePoint >= FULLWIDTH_ASCII_START &&
      codePoint <= FULLWIDTH_ASCII_END
    ) {
      normalized += String.fromCodePoint(codePoint - FULLWIDTH_ASCII_OFFSET);
      continue;
    }

    normalized += char;
  }

  return normalized;
}

function normalizeTrimmedString(value) {
  return normalizeKeyboardCompatibleCharacters(value).trim();
}

function formatCanonicalNumber(value) {
  return String(Number(value.toFixed(6)));
}

function normalizeHeightMeasurementInput(value) {
  const normalized = normalizeTrimmedString(value);

  if (!normalized) {
    return "";
  }

  const explicitCentimetreMatch = normalized.match(EXPLICIT_CENTIMETRE_PATTERN);
  if (explicitCentimetreMatch) {
    const centimetres = Number(explicitCentimetreMatch[1]);
    return Number.isFinite(centimetres) ? formatCanonicalNumber(centimetres) : normalized;
  }

  const explicitMetreMatch = normalized.match(EXPLICIT_METRE_PATTERN);
  if (explicitMetreMatch) {
    const metres = Number(explicitMetreMatch[1]);
    return Number.isFinite(metres) ? formatCanonicalNumber(metres * 100) : normalized;
  }

  if (DECIMAL_NUMBER_PATTERN.test(normalized)) {
    const parsedValue = Number(normalized);
    if (Number.isFinite(parsedValue) && parsedValue > 0 && parsedValue < 3) {
      return formatCanonicalNumber(parsedValue * 100);
    }
  }

  return normalized;
}

export function normalizeClinicStudyIdInput(value) {
  return normalizeTrimmedString(value).toUpperCase();
}

export function formatClinicStudyIdForTyping(value) {
  return normalizeKeyboardCompatibleCharacters(value).toUpperCase();
}

export function normalizeParticipantIdInput(value) {
  return normalizeTrimmedString(value).toUpperCase();
}

export function normalizeMeasurementInput(value, { fieldName } = {}) {
  if (fieldName === "height") {
    return normalizeHeightMeasurementInput(value);
  }

  return normalizeTrimmedString(value);
}

export function normalizeDateInput(value) {
  return normalizeTrimmedString(value);
}

export function normalizeProfileFormData(profile) {
  if (!profile || typeof profile !== "object" || Array.isArray(profile)) {
    return profile;
  }

  const normalizedProfile = { ...profile };

  if (Object.prototype.hasOwnProperty.call(normalizedProfile, "id")) {
    normalizedProfile.id = normalizeParticipantIdInput(normalizedProfile.id);
  }

  if (Object.prototype.hasOwnProperty.call(normalizedProfile, "subjectId")) {
    normalizedProfile.subjectId = normalizeParticipantIdInput(
      normalizedProfile.subjectId,
    );
  }

  if (Object.prototype.hasOwnProperty.call(normalizedProfile, "clinicStudyId")) {
    normalizedProfile.clinicStudyId = normalizeClinicStudyIdInput(
      normalizedProfile.clinicStudyId,
    );
  }

  for (const field of ["height", "weight", "chestCircumference"]) {
    if (Object.prototype.hasOwnProperty.call(normalizedProfile, field)) {
      normalizedProfile[field] = normalizeMeasurementInput(
        normalizedProfile[field],
        { fieldName: field },
      );
    }
  }

  if (Object.prototype.hasOwnProperty.call(normalizedProfile, "dateOfBirth")) {
    normalizedProfile.dateOfBirth = normalizeDateInput(
      normalizedProfile.dateOfBirth,
    );
  }

  return normalizedProfile;
}
